import { useTranslation } from '@/hooks/useTranslation';
import { Calendar } from '@/modules/global-components/ui/Calendar';
import { LabelWrapper } from '@/modules/global-components/ui/wrappers/LabelWrapper';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { PopupProps } from '@/types/component';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  AddTaskInitialValues,
  AddTaskSchema,
  generateAddTaskPayload,
  type AddTaskSchemaType,
} from './AddTaskSchemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useAddTaskMutation } from '@/store/apis/user/tasksApiCalls';
import { useToastContext } from '@/modules/global-components/ui/toast-context/useToastContext';
import { AnimatePresence, motion } from 'motion/react';
import {
  useGetAllCarriersQuery,
  useGetCustomersQuery,
} from '@/store/apis/carriers/carrierApiCalls';
import type { CarrierMaster, CustomerInterface } from '@/types/carrier-types';
import { EllipseLabel } from '@/modules/global-components/ui/wrappers/EllipseLabel';
import { Flag } from '@/modules/global-components/ui/Flag';
import { Tag } from 'primereact/tag';
import type { CarrierTypeForTransport } from '@/types/transport-types';
import { commonActions } from '@/store/slices/commonSlice';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Chip } from 'primereact/chip';
import { TabPanel, TabView } from 'primereact/tabview';
import { InputSwitch } from 'primereact/inputswitch';
import { Link } from 'react-router';
import { goToViewCustomer } from '@/helpers/utils/links/customerLinks';
import { InfoText } from '@/modules/main/carrier/view/components/components/info-text/InfoText';
import { inputColumn2 } from '@/modules/main/carrier/edit/styles';
import { cn, convertNumber } from '@/helpers/utils/functionality-utils';
import { goToViewCarrier } from '@/helpers/utils/links/carriersLinks';
import { statusColors } from '@/constants/carriers-constants';
import { useSearchCountry } from '@/hooks/search-hooks';
import { tabsPT } from '@/helpers/ComponentUtils';
import { useSharedWebSocket } from '@/hooks/socket/useSharedWebSocket';
import type { UsersSocketResponse } from '@/types/authresponse';
import { Avatar } from 'primereact/avatar';
import { getUsers } from '@/services/masters/user/userApiCalls';
import type { User } from '@/types/userlist';
import { AvatarGroup } from 'primereact/avatargroup';
import { Select } from '@/modules/global-components/ui/dropdowns/Select';

const wsReportsUrl = import.meta.env.VITE_WS_REPORTS_URL;

export const AddTaskPopup = ({ open, setOpen }: PopupProps) => {
  const { t } = useTranslation('tasks');
  const [currentIndex, setCurrentIndex] = useState(0);
  const { showError } = useToastContext();
  const [isOpenMoreInfo, setIsOpenMoreInfo] = useState(false);
  const moreInfoRef = useRef<OverlayPanel>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const methods = useForm({
    defaultValues: AddTaskInitialValues,
    resolver: zodResolver(AddTaskSchema),
  });
  const [customer, carrier] = methods.watch(['customer', 'carrier']);
  const [addTask, { isLoading }] = useAddTaskMutation();
  const dispatch = useAppDispatch();
  const customersQuery = useGetCustomersQuery();
  const carriersForTransport = useGetAllCarriersQuery('');
  const { countryMap } = useSearchCountry();

  const usersResponse = useAppSelector((state) => state.user.users) || [];

  const [activeUsers, setActiveUsers] = useState<UsersSocketResponse[]>([]);

  const { rawMessage } = useSharedWebSocket(`${wsReportsUrl}/ws/users`);

  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerInterface | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<CarrierMaster | null>(
    null,
  );

  const [isDueImmediate, setIsDueImmediate] = useState(true);

  function onCancel() {
    methods.reset();
    setOpen(false);
  }

  useEffect(() => {
    if (rawMessage) {
      setActiveUsers(rawMessage);
    }
  }, [rawMessage]);

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  const selectedUsers = methods.watch('assignTo') || [];
  let ui = [<div />];

  if (selectedUsers.length <= 2) {
    ui = selectedUsers.map((user) => {
      return (
        <Chip
          key={user?.user_id}
          label={user?.name}
          className="mr-2"
          icon="pi pi-user"
          image={user?.pic || ''}
        />
      );
    });
  } else {
    ui = [
      <div className="flex items-center gap-3">
        <AvatarGroup>
          {selectedUsers.slice(0, 4).map((user) => {
            return (
              <Avatar
                key={user?.user_id}
                title={user?.name}
                shape="circle"
                icon="pi pi-user"
                size="large"
                image={user?.pic || ''}
              />
            );
          })}
          {selectedUsers.length > 4 && (
            <Avatar
              shape="circle"
              size="large"
              template={
                <div className="text-base">+{selectedUsers.length - 4}</div>
              }
            />
          )}
        </AvatarGroup>
        <div className="flex gap-3 text-xl font-medium">
          {selectedUsers[0]?.name} and {selectedUsers.length - 1} more
        </div>
      </div>,
    ];
  }

  const activeUsersId = activeUsers.map((user) => user.user_id);
  const filteredActiveUsers = usersResponse.data
    .filter((user) => activeUsersId.includes(user.user_id))
    .map((user) => ({ ...user, isActive: true }));
  const filteredInactiveUsers = usersResponse.data
    .filter((user) => !activeUsersId.includes(user.user_id))
    .map((user) => ({ ...user, isActive: false }));

  return (
    <Dialog
      className="w-screen max-w-[700px] bg-surface-card"
      pt={{
        footer: { className: 'mt-5 shadow-[0px_-1px_5px] pt-5' },
      }}
      footer={
        <div className="grid size-full place-items-center">
          <Button
            loading={isLoading}
            type="submit"
            onClick={() => {
              submitRef.current?.click();
            }}
            className="w-full px-10"
            label={t('assign')}
          />
        </div>
      }
      visible={open}
      onHide={() => {
        onCancel();
      }}
      blockScroll
      header={t('assignTask')}
      contentClassName="px-10"
      headerClassName="px-10 mb-5 shadow-pfs-4"
    >
      <form
        onSubmit={methods.handleSubmit(async (data: AddTaskSchemaType) => {
          try {
            await Promise.all(
              data.assignTo.filter(Boolean).map((user) => {
                const payload = generateAddTaskPayload(data, user!.user_id);
                return addTask(payload);
              }),
            ).then(() => {
              dispatch(commonActions.setNewAssignedTaskAdded(true));
              setTimeout(() => {
                dispatch(commonActions.setNewAssignedTaskAdded(false));
              }, 2000);
              onCancel();
            });
          } catch (err) {
            showError(err as string);
          }
        })}
        className="flex flex-col gap-3"
      >
        <Controller
          control={methods.control}
          name="assignTo"
          render={({ field }) => (
            <LabelWrapper
              error={methods.formState.errors.assignTo?.message}
              imp
              label={t('assignTo')}
            >
              <MultiSelect
                {...field}
                filter
                placeholder={t('selectActiveUsers')}
                selectedItemsLabel={t('usersSelected', {
                  count: String(selectedUsers.length),
                })}
                display="chip"
                maxSelectedLabels={3}
                loading={usersResponse.loading}
                options={[...filteredActiveUsers, ...filteredInactiveUsers]}
                optionLabel="name"
                className="w-full"
                pt={{
                  item: {
                    className: 'w-full [&>span]:w-full',
                  },
                }}
                itemTemplate={(item: User & { isActive?: boolean }) => {
                  return (
                    <div className="flex h-8 w-full items-center gap-5">
                      <div
                        className={cn(
                          'relative grid size-[2.5rem] shrink-0 place-items-center rounded-full border-2 p-0.5',
                          item.isActive
                            ? 'border-green-500'
                            : 'border-transparent',
                        )}
                      >
                        {item.isActive && (
                          <div className="absolute -bottom-1 -right-1 size-4 rounded-full border-[3px] border-white bg-green-400" />
                        )}
                        <Avatar
                          image={item.pic || ''}
                          icon="pi pi-user"
                          size="normal"
                          shape="circle"
                        />
                      </div>
                      <span>{item.name}</span>
                    </div>
                  );
                }}
              />
            </LabelWrapper>
          )}
        />
        {convertNumber(selectedUsers.length) > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-1">{ui}</div>
          </div>
        )}
        <Controller
          control={methods.control}
          name="description"
          render={({ field }) => {
            return (
              <LabelWrapper
                error={methods.formState.errors.description?.message}
                imp
                label={t('describeYourTask')}
                containerClassName="text-2xl"
              >
                <InputTextarea
                  placeholder="eg: update carrier 112233 & 445566"
                  autoResize
                  className="h-[200px]"
                  {...field}
                />
              </LabelWrapper>
            );
          }}
        />
        <Controller
          control={methods.control}
          name="dueDate"
          render={({ field }) => (
            <LabelWrapper
              containerClassName="relative"
              error={methods.formState.errors.dueDate?.message}
              imp
              label={t('dueOn')}
            >
              <AnimatePresence initial={false}>
                {!isDueImmediate && (
                  <motion.div
                    className="w-full"
                    transition={{ duration: 0.1 }}
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                  >
                    <Calendar className="w-full" {...field} />
                  </motion.div>
                )}
              </AnimatePresence>
              <label className="absolute -right-1 top-0.5 flex h-5 items-center gap-2 py-1 text-sm">
                {t('dueImmediate')}
                <InputSwitch
                  checked={isDueImmediate}
                  onChange={() => {
                    setIsDueImmediate(!isDueImmediate);
                    if (!isDueImmediate) {
                      field.onChange(new Date());
                    }
                  }}
                />
              </label>
            </LabelWrapper>
          )}
        />
        <div
          onClick={(e) => {
            moreInfoRef?.current?.toggle(e);
            setIsOpenMoreInfo(true);
          }}
          className="-mx-10 mt-5 flex cursor-pointer gap-3 border-y p-5 px-10 hover:bg-surface-100"
        >
          <div className="">
            <i className="pi pi-plus" />
          </div>
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div>{t('linkAResource')}</div>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          {customer && (
            <Chip
              icon={'pi pi-user'}
              label={customer?.name}
              removable
              onRemove={() => {
                methods.setValue('customer', null);
                return true;
              }}
            />
          )}
          {carrier && (
            <Chip
              icon="pi pi-car"
              label={carrier.stock_no}
              removable
              onRemove={() => {
                methods.setValue('carrier', null);
                return true;
              }}
            />
          )}
        </div>
        <Dialog
          visible={isOpenMoreInfo}
          onHide={() => setIsOpenMoreInfo(false)}
          className="w-[450px]"
          header={t('tasksMoreInfo')}
          footer={
            <div className="flex size-full justify-end">
              {currentIndex === 0 ? (
                <Button
                  disabled={!selectedCustomer}
                  onClick={() => {
                    if (selectedCustomer) {
                      methods.setValue('customer', selectedCustomer);
                      setSelectedCustomer(null);
                    }
                  }}
                  type="button"
                >
                  {t('addCustomer')}
                </Button>
              ) : (
                <Button
                  disabled={!selectedCarrier}
                  onClick={() => {
                    if (selectedCarrier) {
                      methods.setValue('carrier', selectedCarrier);
                      setSelectedCarrier(null);
                    }
                  }}
                  type="button"
                >
                  {t('addCarrier')}
                </Button>
              )}
            </div>
          }
        >
          <TabView
            activeIndex={currentIndex}
            onTabChange={(e) => setCurrentIndex(e.index)}
          >
            <TabPanel
              pt={tabsPT}
              headerTemplate={(e) => (
                <div
                  onClick={e.onClick}
                  className={cn(e.className, 'flex items-center gap-4')}
                >
                  {t('customer')}
                </div>
              )}
            >
              <Select
                virtualScrollerOptions={{ itemSize: 38 }}
                optionLabel="name"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.value)}
                pt={{
                  itemLabel(options) {
                    return {
                      ...options,
                      className: 'flex w-full',
                    };
                  },
                  list: {
                    className: 'w-full',
                  },
                }}
                loading={customersQuery.isLoading}
                label={t('Customer')}
                options={customersQuery.data?.data || []}
                itemTemplate={(customer: CustomerInterface) => {
                  const obj = customer;
                  return (
                    <div className="flex w-full items-center justify-between gap-4">
                      <EllipseLabel label={obj?.name} />
                      <div className="flex shrink-0 gap-2">
                        <Flag code={obj?.country_code} />
                        <Tag>{obj?.contact_type}</Tag>
                      </div>
                    </div>
                  );
                }}
                valueTemplate={(customer: CustomerInterface, p) => {
                  if (!customer) {
                    return p.placeholder;
                  }
                  const obj = customer;
                  return (
                    <div className="flex w-full items-center justify-between gap-4">
                      <EllipseLabel label={obj?.name} />
                      <div className="flex shrink-0 gap-2">
                        <Flag code={obj?.country_code} />
                        <Tag>{obj?.contact_type}</Tag>
                      </div>
                    </div>
                  );
                }}
                placeholder={t('customer')}
              />
              {!selectedCustomer && (
                <div className="my-3 grid size-full h-32 place-items-center rounded-xl border-4 border-dashed border-surface-300 text-surface-400">
                  {t('chooseACustomer')}
                </div>
              )}
              <div
                className={cn(
                  'my-3 flex flex-wrap gap-4',
                  !selectedCustomer?.id && 'hidden',
                )}
              >
                {selectedCustomer?.name && (
                  <InfoText
                    containerClassName={inputColumn2}
                    text={
                      <div className="text-lg">
                        <Link to={goToViewCustomer(selectedCustomer?.id)}>
                          {selectedCustomer?.name}
                        </Link>
                      </div>
                    }
                    title={t('name')}
                  />
                )}
                {selectedCustomer?.contact_type && (
                  <InfoText
                    containerClassName={inputColumn2}
                    text={
                      <div className="text-lg">
                        {selectedCustomer?.contact_type}
                      </div>
                    }
                    title={t('contactType')}
                  />
                )}
                {selectedCustomer?.currency && (
                  <InfoText
                    containerClassName={inputColumn2}
                    text={
                      <div className="text-lg">
                        {selectedCustomer?.currency}
                      </div>
                    }
                    title={t('currency')}
                  />
                )}
                {selectedCustomer?.country_code && (
                  <InfoText
                    containerClassName={inputColumn2}
                    text={
                      selectedCustomer?.country_code && (
                        <div className="flex items-center gap-2 text-lg">
                          <Flag code={selectedCustomer?.country_code} />
                          {countryMap[selectedCustomer?.country_code]}
                        </div>
                      )
                    }
                    title={t('country')}
                  />
                )}
              </div>
            </TabPanel>
            <TabPanel
              pt={tabsPT}
              headerTemplate={(e) => (
                <div
                  onClick={e.onClick}
                  className={cn(e.className, 'flex items-center gap-4')}
                >
                  {t('carrier')}
                </div>
              )}
            >
              <Select
                virtualScrollerOptions={{ itemSize: 38 }}
                loading={carriersForTransport.isLoading}
                value={selectedCarrier}
                onChange={(e) => setSelectedCarrier(e.value)}
                label={t('chooseStockNo')}
                options={carriersForTransport.data?.data || []}
                filterBy="stock_no,chassis_model,chassis_no"
                itemTemplate={(item: CarrierTypeForTransport) => (
                  <div className="flex items-center gap-1">
                    <div>{item.stock_no}</div>
                    {item.chassis_model && (
                      <div>
                        {'- '}
                        {item.chassis_model}
                      </div>
                    )}
                    {item.chassis_no && (
                      <div>
                        {'-  '}
                        {item.chassis_no}
                      </div>
                    )}
                  </div>
                )}
                valueTemplate={(item: CarrierTypeForTransport, p) => {
                  if (!item) {
                    return p?.placeholder;
                  }
                  return (
                    <div className="flex items-center gap-1">
                      <div>{item.stock_no}</div>
                      {item.chassis_model && (
                        <div>
                          {'-  '}
                          {item.chassis_model}
                        </div>
                      )}
                      {item.chassis_no && (
                        <div>
                          {'-  '}
                          {item.chassis_no}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <div
                className={cn(
                  'my-3 flex flex-wrap gap-4',
                  !selectedCarrier?.carrier_id && 'hidden',
                )}
              >
                {selectedCarrier?.stock_no && (
                  <InfoText
                    title={t('stockNo')}
                    text={
                      <Link
                        to={goToViewCarrier(selectedCarrier?.carrier_id)}
                        className="text-lg"
                      >
                        {selectedCarrier?.stock_no}
                      </Link>
                    }
                    containerClassName={inputColumn2}
                  />
                )}
                {selectedCarrier?.chassis && (
                  <InfoText
                    title={t('Chassis')}
                    text={
                      <div className="text-lg">{selectedCarrier?.chassis}</div>
                    }
                    containerClassName={inputColumn2}
                  />
                )}
                {selectedCarrier?.status && (
                  <InfoText
                    title={t('status')}
                    text={
                      <Tag
                        rounded
                        className={`w-fit px-4 py-2 ${
                          statusColors[
                            selectedCarrier?.status as keyof typeof statusColors
                          ]
                        }`}
                      >
                        {String(selectedCarrier?.status).toUpperCase()}
                      </Tag>
                    }
                    containerClassName={inputColumn2}
                  />
                )}
                {selectedCarrier?.make_model && (
                  <InfoText
                    title={t('makeModel')}
                    text={
                      <div className="text-lg">
                        {selectedCarrier?.make_model}
                      </div>
                    }
                    containerClassName={inputColumn2}
                  />
                )}
              </div>
              {!selectedCarrier && (
                <div className="my-3 grid size-full h-32 place-items-center rounded-xl border-4 border-dashed border-surface-300 text-surface-400">
                  {t('chooseACarrier')}
                </div>
              )}
            </TabPanel>
          </TabView>
        </Dialog>
        <button type="submit" ref={submitRef} className="hidden" />
      </form>
    </Dialog>
  );
};
